import React, { useRef, useState } from "react";
import {
  AppBar,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RichEditor from "../../generic/rich-editor";

// Edits the text of one document the PQRSD module files by itself — the body
// only. The membrete (header, footer and the empty 1x2 table RoyalEditorApi
// stamps the radicado and QR into) lives in its own file; PqrsdDocuments pours
// this text into it once the dialog closes, so nothing typed here needs a
// header, and any header typed here is dropped.
//
// The editor autosaves over the text's storage path while typing.

// Preview values for the mail-merge pane: the admin sees the shape of a real
// requerimiento instead of raw placeholders while writing.
const SAMPLE = {
  radicado_pqrsd: "PQR_20260912-00411",
  fecha: "12 de septiembre de 2026",
  fecha_radicacion: "11 de septiembre de 2026",
  asunto: "Solicitud de certificado de notas",
  nombre_solicitante: "María José",
  apellido_solicitante: "Peña Ríos",
  email: "maria.pena@example.com",
  tipo_solicitud: "Petición general",
  puntos_requeridos:
    "1. ¿En qué programa estuvo matriculada? (obligatorio)\n2. Documento: Cédula (obligatorio)",
  numero_puntos: "2",
  observaciones: "Sin estos datos no podemos identificar su historia académica.",
  fecha_limite: "12 de octubre de 2026",
  dias_plazo: "30",
  enlace_respuesta: "https://royalplusdev.web.app/pqrsd/estado",
  fundamento: "Artículo 17 de la Ley 1755 de 2015",
  motivacion: "El peticionario no aportó la información requerida dentro del plazo concedido.",
  radicado_requerimiento: "PQR_20260912-00500",
  // Radicación
  radicado: "PQR_20260911-00411",
  tipo_id: "CC",
  num_id: "1.036.555.444",
  type_person: "natural",
  body: "Solicito el certificado de notas del programa de Tecnología en Gestión Administrativa.",
  annexes_names: "• Anexo 1: cedula.pdf",
  // Recurso de reposición
  fecha_limite_recurso: "26 de septiembre de 2026",
  radicado_desistimiento: "PQR_20260912-00612",
  fecha_desistimiento: "12 de septiembre de 2026",
  fecha_recurso: "20 de septiembre de 2026",
  motivos_recurso: "No recibí el correo a tiempo; aporto la información solicitada.",
  respuestas_recurso: "1. ¿En qué programa estuvo matriculada?\n     Respuesta: Tecnología en Gestión Administrativa\n2. Documento: Cédula — aportado (cedula.pdf)",
  decision: "REPONER el acto PQR_20260912-00612 del 12 de septiembre de 2026, por el cual se decretó el desistimiento tácito de la petición PQR_20260911-00411, y en consecuencia continuar su trámite con la información aportada por el peticionario.",
  fecha_requerimiento: "12 de septiembre de 2026",
  firmante: "Ana Gómez",
  cargo_firmante: "Profesional Universitario",
  dependencia_firmante: "Ventanilla Única",
  proyecto: "Ana Gómez",
  reviso: "Carlos Pérez",
  // Plantillas de respuesta
  asunto_pqrsd: "Solicitud de certificado de notas",
  descripcion_pqrsd: "Solicito el certificado de notas del programa de Tecnología en Gestión Administrativa.",
  anexos_pqrsd: "• Anexo 1: cedula.pdf",
  tipo_respuesta: "Respuesta final",
  nombre_destinatario: "María José",
  apellido_destinatario: "Peña Ríos",
  correo_destinatario: "maria.pena@example.com",
};

export default function PqrsdTemplateDialog({ open, onClose, kind, label, file, tags = [], onSaved }) {
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [closing, setClosing] = useState(false);
  const editorRef = useRef(null);
  const closeAfterSave = useRef(false);

  const handleSave = (url) => {
    setSavedAt(new Date());
    setError(null);
    if (url && onSaved) onSaved(kind, url);
    if (closeAfterSave.current) {
      closeAfterSave.current = false;
      onClose();
    }
  };

  // Autosave waits for a pause in typing; closing inside that pause would drop
  // the last words and rebuild the document without them. Flush first.
  const handleClose = () => {
    const editor = editorRef.current;
    if (!editor?.hasUnsavedChanges) return onClose();
    // RichEditor flags a brand-new file as unsaved on open; an untouched blank
    // page must not be stored as the document's text.
    if (!file?.url && (editor.document?.length ?? 0) <= 1) return onClose();
    setClosing(true);
    closeAfterSave.current = true;
    try {
      editor.saveDocument();
    } catch (_) {
      closeAfterSave.current = false;
      onClose();
      return;
    }
    // A save that never reports back must not trap the admin in the dialog.
    setTimeout(() => {
      if (!closeAfterSave.current) return;
      closeAfterSave.current = false;
      setError("No se pudo confirmar el guardado de los últimos cambios.");
      setClosing(false);
    }, 15000);
  };

  return (
    <Dialog fullScreen open={open} onClose={handleClose}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar sx={{ gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
              {label}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              Escriba solo el texto. El membrete asignado se aplica automáticamente al cerrar.
            </Typography>
          </Box>
          {savedAt && (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              icon={<CheckCircleIcon />}
              label={`Guardado ${savedAt.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}`}
            />
          )}
          <Button onClick={handleClose} variant="contained" disabled={closing}>
            {closing ? "Guardando…" : "Listo"}
          </Button>
          <Tooltip title="Cerrar">
            <span>
              <IconButton onClick={handleClose} disabled={closing}><CloseIcon /></IconButton>
            </span>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2, pb: 0 }}>
        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
        <Alert severity="info" sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Escriba el texto tal como debe salir, y donde deba ir un dato escriba la etiqueta entre
            dobles ángulos. Se guarda solo.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
            {tags.map((tag) => (
              <Chip key={tag} size="small" label={`<<${tag}>>`} sx={{ fontFamily: "monospace" }} />
            ))}
          </Box>
        </Alert>
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        {/* Keyed on the file path so switching between the two templates
            remounts the editor instead of reusing the previous document. */}
        <RichEditor
          key={file?.path}
          file={file}
          tags={tags}
          dataSource={SAMPLE}
          height="calc(100vh - 260px)"
          onSave={handleSave}
          setEditorRef={(editor) => { editorRef.current = editor; }}
        />
      </Box>
    </Dialog>
  );
}
